import React, { createContext, useContext, useRef, useState } from "react";

import { useForm } from "@pages/User/NewRequest/resources/context/FormContext";

import { useIoCContext } from "@context/IoCContext/IoCContext";
import useDialogAlert from "@hooks/useDialogAlert";

import {
  IAvailableDatesRequest,
  IAvailableDatesResponse,
  IGetClientInfoService,
} from "@modules/orders/models/IGetClientInfoService";

import { useDebouncedEffect } from "@hooks/useDebouncedEffect";

import AppError from "@utils/AppError";
import { Types } from "@ioc/types";

interface IAvailableDatesContext {
  availableDates: IAvailableDatesResponse | null;
}

interface IAvailableDatesProvider {
  children: React.ReactNode;
}

const AvailableDatesContext = createContext<IAvailableDatesContext | null>(
  null
);

const ClientType = Types.Orders.IGetClientInfoService;

const AvailableDatesProvider: React.FC<IAvailableDatesProvider> = (props) => {
  const { children } = props;

  const { snackbar } = useDialogAlert();
  const { serviceContainer: service } = useIoCContext();

  const { dataCustomer, selectedWithDrawalBase, freightSelected } = useForm();

  const [
    availableDates,
    setAvailableDates,
  ] = useState<IAvailableDatesResponse | null>(null);

  const lastRequestSignature = useRef<string | null>(null);

  const fetchAvailableDates = async (
    query: IAvailableDatesRequest
  ): Promise<IAvailableDatesResponse> => {
    try {
      const getClient = service.get<IGetClientInfoService>(ClientType);
      const response = await getClient.availableDates(query);
      setAvailableDates(response);
      return response;
    } catch (error) {
      if (error instanceof AppError) {
        snackbar({
          message: `Erro ao buscar dias disponíveis:  - ${error?.message}`,
          variant: "error",
        });
      }
      setAvailableDates(null);
      throw error;
    }
  };

  const buildAvailableDatesQuery = (): IAvailableDatesRequest | null => {
    const CNPJ = dataCustomer?.CNPJ ?? "";
    const withdrawBasisID = selectedWithDrawalBase?.value ?? "";
    const freightType = freightSelected ?? "";

    const isValidString = (val?: string): val is string =>
      typeof val === "string" && val.trim() !== "";

    const acceptValidString =
      !isValidString(CNPJ) ||
      !isValidString(freightType) ||
      !isValidString(withdrawBasisID);

    if (acceptValidString) return null;

    // Note: Gera uma "assinatura única" da requisição
    const signature = `${CNPJ}-${freightType}-${withdrawBasisID}`;

    // Note: Evita chamadas duplicadas para os mesmos dados
    if (lastRequestSignature.current === signature) return null;

    lastRequestSignature.current = signature;

    return {
      CNPJ,
      freightType,
      withdrawBasisID,
    };
  };

  const autoFetchAvailableDates = async () => {
    const query = buildAvailableDatesQuery();
    if (query) {
      await fetchAvailableDates(query);
    }
  };

  useDebouncedEffect(
    () => {
      autoFetchAvailableDates();
    },
    [dataCustomer, selectedWithDrawalBase, freightSelected],
    300
  );

  return (
    <AvailableDatesContext.Provider
      value={{
        availableDates,
      }}
    >
      {children}
    </AvailableDatesContext.Provider>
  );
};

const useAvailableDates = (): IAvailableDatesContext => {
  const context = useContext(AvailableDatesContext);
  if (!context) {
    throw new Error(
      "useAvailableDates deve ser utilizado dentro de um AvailableDatesProvider"
    );
  }
  return context;
};

export { AvailableDatesProvider, useAvailableDates };
